import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slider,
  Alert,
  Tooltip,
} from '@mui/material';
import { Close, ContentCopy, Token } from '@mui/icons-material';
import { getApiUrl } from '../config';

const TokenCreator = ({ sessionData, authData, open, onClose }) => {
  const [expiryDays, setExpiryDays] = useState(7);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const handleCreateToken = async () => {
    setLoading(true);
    setError('');
    setGeneratedToken(null);

    try {
      const response = await fetch(getApiUrl('/auth/create-character-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData.sessionToken}`,
        },
        body: JSON.stringify({
          displayName: sessionData.selectedDisplayName,
          expiryDays: expiryDays,
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setGeneratedToken(result);
      } else {
        setError(result.message || 'Failed to create token');
      }
    } catch (err) {
      console.error('Token creation error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevokeAllTokens = async () => {
    setRevoking(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/auth/revoke-all-tokens'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData.sessionToken}`,
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setRevokeDialogOpen(false);
        handleClose();
        // Show success message and potentially redirect to login
        alert(result.message || 'All tokens have been revoked. You will need to log in again.');
        // Redirect to login by reloading the page (clears session)
        window.location.reload();
      } else {
        setError(result.message || 'Failed to revoke tokens');
        setRevokeDialogOpen(false);
      }
    } catch (err) {
      console.error('Token revocation error:', err);
      setError('Failed to connect to server');
      setRevokeDialogOpen(false);
    } finally {
      setRevoking(false);
    }
  };

  const handleClose = () => {
    setGeneratedToken(null);
    setError('');
    setCopied(false);
    setRevokeDialogOpen(false);
    onClose();
  };

  const formatExpiryDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Token />
            <Typography variant="h6">
              Create Character Token
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!generatedToken ? (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Create a long-lived authentication token for <strong>{sessionData.selectedDisplayName}</strong>.
              This token can be used for API access or external integrations.
            </Typography>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography gutterBottom>
                Token Expiry: {expiryDays} {expiryDays === 1 ? 'day' : 'days'}
              </Typography>
              <Slider
                value={expiryDays}
                onChange={(e, value) => setExpiryDays(value)}
                min={1}
                max={30}
                marks={[
                  { value: 1, label: '1 day' },
                  { value: 7, label: '7 days' },
                  { value: 14, label: '14 days' },
                  { value: 30, label: '30 days' },
                ]}
                valueLabelDisplay="auto"
                disabled={loading}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Token created successfully!
            </Alert>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Character: <strong>{generatedToken.displayName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Expires: <strong>{formatExpiryDate(generatedToken.expiresAt)}</strong>
            </Typography>

            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Your Token (copy and store securely):
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  wordBreak: 'break-all',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                {generatedToken.token}
                <Tooltip title={copied ? "Copied!" : "Copy token"}>
                  <IconButton
                    onClick={handleCopyToken}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                    }}
                    size="small"
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Store this token securely. You won't be able to see it again.
                Anyone with this token can authenticate as {generatedToken.displayName}.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        {!generatedToken ? (
          <>
            <Button
              onClick={() => setRevokeDialogOpen(true)}
              color="error"
              disabled={loading}
            >
              Revoke All Tokens
            </Button>
            <Box>
              <Button onClick={handleClose} disabled={loading} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateToken}
                variant="contained"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Token'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box />
            <Box>
              <Button onClick={handleCopyToken} startIcon={<ContentCopy />} sx={{ mr: 1 }}>
                Copy Token
              </Button>
              <Button onClick={handleClose} variant="contained">
                Done
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>

    {/* Revoke Confirmation Dialog */}
    <Dialog
      open={revokeDialogOpen}
      onClose={() => !revoking && setRevokeDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} color="error.main">
          <Token />
          <Typography variant="h6">
            Revoke All Tokens?
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This action cannot be undone!
          </Typography>
        </Alert>
        <Typography variant="body1" gutterBottom>
          This will invalidate:
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 2 }}>
          <li>Your current session token</li>
          <li>All previously created character tokens</li>
          <li>All active API integrations</li>
        </Box>
        <Typography variant="body2" color="text.secondary">
          You will be logged out immediately and need to sign in again to regain access.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRevokeDialogOpen(false)} disabled={revoking}>
          Cancel
        </Button>
        <Button
          onClick={handleRevokeAllTokens}
          color="error"
          variant="contained"
          disabled={revoking}
        >
          {revoking ? 'Revoking...' : 'Yes, Revoke All Tokens'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TokenCreator;
